import React, { ReactElement, useMemo } from 'react';
import classNames from 'classnames';
import { TFunction } from 'i18next';
import EditForm from 'components/editForm/EditForm';
import {
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    Option,
    ValidateValueType,
    ValuesInterface,
} from 'components/interfaces/GeneralInterface';
import {
    getTranslation,
    getTranslationKey,
    useTranslation,
} from 'utils/localization';
import {
    validateEmail,
    validateNotRequiredPhone,
    validatePassword,
} from 'utils/index';
import {
    FieldTypeApiEnum,
    MemberRegistrationApiInterface,
    RepresentativeRelationEnum,
} from 'submodules/api_middleware';
import { FormResponseData, UserService } from 'services/user.service';
import PasswordHelp from 'components/login/signup/PasswordHelp';
import stylesBasicBlock from 'components/blocks/BasicDetailsBlock.module.scss';
import { EditFormDataInterface } from 'components/interfaces/EditFormInterface';
import { ROW_BREAK } from 'components/editForm/utils/items';
import { TNamespaceEnum } from 'types/types';

interface Props {
    getRequestData: () => MemberRegistrationApiInterface;
    goNext: () => void;
    titleKey: string;
    /**
     * Is currently registered member a minor?
     */
    isMinor: boolean;
    values: ValuesInterface;
    validateCzechPhoneFormat: boolean;
    hidePassword: boolean;
    goBack: () => void;
    getFormData: (data: EditFormDataInterface) => void;
}
/**
 * Component to input the credentials for the user of currently registered
 * person. Once user clicks on next, the form data are submitted to the backend.
 */
const SignUpLogInDetails = ({
    getRequestData,
    goNext,
    titleKey,
    isMinor,
    values,
    validateCzechPhoneFormat,
    goBack,
    hidePassword,
    getFormData,
}: Props): ReactElement => {
    const { t } = useTranslation();

    const fields = useMemo<GroupEditFieldInterface[]>(
        (): GroupEditFieldInterface[] => [
            ...(isMinor ? getRepresentativeNameGroupFields(t, values) : []),
            ...getFieldWithLocalization(
                t,
                values,
                validateCzechPhoneFormat,
                false
            ),
        ],
        [validateCzechPhoneFormat, titleKey, hidePassword]
    );

    const submit = async (): Promise<FormResponseData> => {
        const data: MemberRegistrationApiInterface = getRequestData();

        const result = await new UserService().registerCheck(data);

        if (result.errors) {
            if (result.errors['data.person.national_identifier'])
                return {
                    error: getTranslation(
                        t,
                        TNamespaceEnum.signup,
                        'birthNumberAlreadyTaken'
                    ),
                };
            else if (result.errors['data.person.phone'])
                return {
                    error: getTranslation(
                        t,
                        TNamespaceEnum.signup,
                        'phoneAlreadyTaken'
                    ),
                };
        }

        return result;
    };

    const getSideBlock = (data: EditFormDataInterface) => {
        if (hidePassword)
            return (
                <div className={classNames(stylesBasicBlock.sizeP50)}>
                    {getTranslation(t, TNamespaceEnum.signup, 'youngLegalNote')}
                </div>
            );
        const password: string =
            data.state.password || data.state.password === 0
                ? String(data.state.password)
                : '';
        return (
            <div className={classNames(stylesBasicBlock.sizeP50)}>
                <PasswordHelp password={password} />
            </div>
        );
    };

    return (
        <EditForm
            fields={fields}
            buttonTextKey={getTranslation(
                t,
                TNamespaceEnum.signup,
                'goToActivation'
            )}
            submit={submit}
            onSuccess={goNext}
            getSideBlock={getSideBlock}
            classContent={stylesBasicBlock.sizeP50}
            goBack={goBack}
            getFormData={getFormData}
        />
    );
};

export default SignUpLogInDetails;

const getRepresentativeNameGroupFields = (
    t: TFunction,
    values: ValuesInterface
): GroupEditFieldInterface[] => [
    {
        title: getTranslationKey(
            TNamespaceEnum.signup,
            'authorizedRepresentative'
        ),
        fields: [
            {
                name: 'representative_relation',
                value: values['representative_relation'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'representativeRelationToRegisteredMinorLabel'
                ),
                type: FieldTypeApiEnum.RADIO,
                size: FilterSize.FULL,
                labelSize: LabelSize.TOP,
                values: [
                    RepresentativeRelationEnum.PARENT,
                    RepresentativeRelationEnum.GUARDIAN,
                    RepresentativeRelationEnum.CUSTODIAN,
                    RepresentativeRelationEnum.OTHER,
                ].map(
                    (type): Option => ({
                        label: t(`RepresentativeRelation.${type}`),
                        value: type,
                    })
                ),
                required: true,
            },
            ROW_BREAK,
            {
                name: 'representative_first_name',
                value: values['representative_first_name'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'representativeFirstName'
                ),
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: true,
            },
            ROW_BREAK,
            {
                name: 'representative_last_name',
                value: values['representative_last_name'],
                label: getTranslationKey(
                    TNamespaceEnum.signup,
                    'representativeLastName'
                ),
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: true,
            },
        ],
    },
];

const getFieldWithLocalization = (
    t: TFunction,
    values: ValuesInterface,
    validateCzechPhoneFormat: boolean,
    hidePassword: boolean
): GroupEditFieldInterface[] => [
    {
        title: getTranslationKey(TNamespaceEnum.signup, 'logInDetails'),
        fields: [
            {
                name: 'email',
                value: values['email'],
                label: 'address.email',
                type: FieldTypeApiEnum.TEXT,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: true,
                validate: (value: ValidateValueType) =>
                    validateEmail(t, String(value)),
            },
            ROW_BREAK,
            ...(hidePassword
                ? []
                : [
                      {
                          name: 'password',
                          value: values['password'],
                          label: getTranslationKey(
                              TNamespaceEnum.signup,
                              'fields.password'
                          ),
                          type: FieldTypeApiEnum.PASSWORD,
                          size: FilterSize.S20,
                          labelSize: LabelSize.TOP,
                          required: true,
                          validate: (value: ValidateValueType) =>
                              validatePassword(String(value), t),
                          autocomplete: 'new-password',
                          triggerAllValidations: true,
                      },
                      ROW_BREAK,
                      {
                          name: 'password_repeated',
                          value: values['password_repeated'],
                          label: getTranslationKey(
                              TNamespaceEnum.signup,
                              'repeatPassword'
                          ),
                          type: FieldTypeApiEnum.PASSWORD,
                          size: FilterSize.S20,
                          labelSize: LabelSize.TOP,
                          required: true,
                          validate: (
                              value:
                                  | string
                                  | string[]
                                  | number
                                  | boolean
                                  | Date,
                              state: any
                          ) =>
                              String(value) === state['password']
                                  ? undefined
                                  : t('validations.passwordNotMatch'),
                      },
                      ROW_BREAK,
                  ]),
            {
                name: 'phone',
                value: values['phone'],
                label: 'address.phone',
                type: FieldTypeApiEnum.PHONE,
                size: FilterSize.S20,
                labelSize: LabelSize.TOP,
                required: false,
                placeholder: validateCzechPhoneFormat ? '+420' : '',
                validate: (value: ValidateValueType) =>
                    validateNotRequiredPhone(
                        value as string,
                        validateCzechPhoneFormat,
                        t
                    ),
            },
        ],
    },
];
